import {
  lazy,
  Suspense,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn, Flex, Markdown, Spinner, Text } from '@kognitos/lattice';
import { stringify } from 'yaml';
import { useIntersectionObserver } from 'usehooks-ts';
import { detectFileType } from '@/modules/file-renderer/utils';
import { EFileType } from '@/modules/file-renderer/types';
import {
  getResolvedFileDisplayName,
  isFile,
  isHtmlString,
  isRemoteValue,
  isMarkdownString,
  shouldRenderInline,
} from '@/modules/outputs/utils';
import {
  OUTPUT_YAML_OPTIONS,
  MARKDOWN_PREVIEW_CLASSNAME,
  MARKDOWN_PREVIEW_MAX_LINES,
} from '@/modules/outputs/constants';
import {
  EOutputCardVariant,
  ERenderMode,
  type IFileRendererProps,
  type TResolvedValue,
} from '@/modules/outputs/types';
import {
  getOutputFileNames,
  getReadableFileName,
  getOutputSourceText,
  getOutputTablePreview,
} from './output-card-utils';
import {
  getOutputPresentation,
  type IOutputPresentation,
} from './output-presentation';
import { IDPValueThumbnail } from './renderers/IDPDocumentThumbnail';
import { OutputMediaPreview } from './output-preview-primitives';
import { useIsOverflowing } from '../hooks/useIsOverflowing';
import { OutputTableCell } from './OutputTableCell';
import { getNumericOutputColumns } from './output-table-order';
import { formatDisplayName } from '@/shared/utils';
import {
  findAutolinks,
  showAutolinksLiterally,
} from './renderers/literal-autolinks';

const FileRenderer = lazy(() =>
  import('./renderers/FileRenderer').then(module => ({
    default: module.FileRenderer,
  })),
);

const OutputRenderer = lazy(() =>
  import('./OutputRenderer').then(module => ({
    default: module.OutputRenderer,
  })),
);

/**
 * Usable height inside a run-table cell: the grid's 112px row less the 28px
 * the preview wrapper insets. Rendered media has no intrinsic height, so it
 * takes this; text-shaped previews size to content and stop here.
 */
const PREVIEW_HEIGHT = 'h-[84px]';
const PREVIEW_MAX_HEIGHT = 'max-h-[84px]';

/** Load report bytes only after the thumbnail enters the viewport. */
function HtmlFileCellPreview({ value }: Pick<IFileRendererProps, 'value'>) {
  const elementRef = useRef<HTMLDivElement>(null);
  const { ref, isIntersecting } = useIntersectionObserver({
    freezeOnceVisible: true,
  });
  // Flex accepts an object ref; expose its element to the observer callback.
  useImperativeHandle<Element | null, HTMLDivElement | null>(
    ref,
    () => elementRef.current,
    [],
  );

  return (
    <Flex
      ref={elementRef}
      direction="column"
      align="stretch"
      width="full"
      overflow="hidden"
      className={cn(PREVIEW_HEIGHT, 'pointer-events-none')}
      inert
    >
      {isIntersecting && (
        <Suspense fallback={null}>
          <FileRenderer
            value={value}
            mode={ERenderMode.PREVIEW}
            previewScale={0.35}
            fillContainer
          />
        </Suspense>
      )}
    </Flex>
  );
}

interface IRunTableOutputPreviewProps {
  name: string;
  value: TResolvedValue;
  onOpen?: () => void;
  /**
   * `table` — a pinned column in the run history table (the automation
   * dashboard and the "Run history" dialog), inside a fixed-height grid cell
   * that owns the surrounding inset.
   * `bare` — a step's output in the run log, which supplies its own spacing
   * and wants the preview to fill it without a second inset.
   * `compact` — `bare`, clipped with a fade instead of scrolling, for panels
   * that already scroll.
   */
  variant?: 'table' | 'bare' | 'compact';
  /**
   * Renders values `shouldRenderInline` claims (live or visual content, such
   * as a browser screencast) in full instead of as a summary. Off in the
   * table, whose fixed-height cells can't fit them.
   */
  renderInline?: boolean;
  /** Extra classes for the outer wrapper, e.g. a width cap from the host. */
  className?: string;
}

function PreviewLines({
  lines,
  mono = false,
}: {
  lines: string[];
  mono?: boolean;
}) {
  return (
    <div className={cn('space-y-0.5', mono && 'font-mono')}>
      {lines.map((line, index) => (
        <div
          // Two files can share a name and code lines repeat, so the text is
          // not unique; position is.
          // eslint-disable-next-line react-x/no-array-index-key -- reason: preview lines are not unique
          key={`${line}-${index}`}
          className="truncate text-xs leading-4"
        >
          {line}
        </div>
      ))}
    </div>
  );
}

type TSyntaxToken = {
  text: string;
  kind: 'key' | 'string' | 'number' | 'muted';
};

/** Mirrors the compact YAML coloring used by Run Assistant. */
function tokenizeSyntaxLine(line: string): TSyntaxToken[] {
  if (line.trimStart().startsWith('#')) return [{ text: line, kind: 'muted' }];

  const yamlMatch = line.match(/^(\s*(?:-\s+)?[^:]+:)(.*)$/);
  if (yamlMatch) {
    const value = yamlMatch[2];
    const kind = /^\s*-?\d+(?:\.\d+)?\s*$/.test(value)
      ? 'number'
      : value.trim().length === 0
        ? 'muted'
        : 'string';
    return [
      { text: yamlMatch[1], kind: 'key' },
      { text: value, kind },
    ];
  }

  const tokens: TSyntaxToken[] = [];
  const tokenPattern = /("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|-?\d+(?:\.\d+)?)/g;
  let cursor = 0;
  for (const match of line.matchAll(tokenPattern)) {
    const index = match.index ?? 0;
    if (index > cursor) {
      tokens.push({ text: line.slice(cursor, index), kind: 'muted' });
    }
    tokens.push({
      text: match[0],
      kind: /^-?\d/.test(match[0]) ? 'number' : 'string',
    });
    cursor = index + match[0].length;
  }
  if (cursor < line.length) {
    tokens.push({ text: line.slice(cursor), kind: 'muted' });
  }
  return tokens.length > 0 ? tokens : [{ text: line, kind: 'muted' }];
}

function SyntaxPreviewLines({ lines }: { lines: string[] }) {
  return (
    <div className="font-mono text-xs leading-4">
      {lines.map((line, lineIndex) => (
        <div
          // eslint-disable-next-line react-x/no-array-index-key -- serialized source lines retain their stable order
          key={lineIndex}
          // `truncate` would collapse the leading spaces that carry YAML nesting.
          className="overflow-hidden text-ellipsis whitespace-pre"
        >
          {tokenizeSyntaxLine(line).map((token, tokenIndex) => (
            <span
              // eslint-disable-next-line react-x/no-array-index-key -- tokens are ordered slices of one immutable source line
              key={tokenIndex}
              className={`output-card__yaml-${token.kind}`}
            >
              {token.text}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function TableCellPreview({
  value,
  presentation,
}: {
  value: TResolvedValue;
  presentation: IOutputPresentation;
}) {
  const preview = getOutputTablePreview(value);
  if (preview.headers.length === 0) {
    return <PreviewLines lines={[presentation.summary]} />;
  }
  const visibleHeaders = preview.headers.slice(0, 3);
  // The cells of a quantity column right-align, its label does not; the
  // values say which columns those are.
  const numericColumns = getNumericOutputColumns(visibleHeaders, preview.rows);
  return (
    <div className="-m-3 text-xs leading-4">
      <div
        className="grid border-b border-border/60 bg-muted/50 px-2 py-1 font-medium text-muted-foreground"
        style={{
          gridTemplateColumns: `repeat(${visibleHeaders.length}, minmax(0, 1fr))`,
        }}
      >
        {visibleHeaders.map(header => (
          <span key={header} className="truncate">
            {formatDisplayName(header)}
          </span>
        ))}
      </div>
      {preview.rows.slice(0, 2).map(row => (
        <div
          key={row.join('\u001f')}
          className="grid border-b border-border/40 px-2 py-1 last:border-b-0"
          style={{
            gridTemplateColumns: `repeat(${visibleHeaders.length}, minmax(0, 1fr))`,
          }}
        >
          {row.slice(0, visibleHeaders.length).map((cell, cellIndex) => (
            <span
              key={visibleHeaders[cellIndex]}
              className={cn(
                'truncate',
                numericColumns[cellIndex] && 'text-right',
              )}
            >
              <OutputTableCell
                compact
                header={visibleHeaders[cellIndex]}
                value={cell}
              />
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * A deliberately inert HTML thumbnail. The empty iframe sandbox disables
 * scripts and navigation, while the CSP prevents the output from initiating
 * network requests for images, styles, fonts, or other embedded resources.
 */
function HtmlCellPreview({
  t,
  html,
  onClippedChange,
}: {
  t: TFunction;
  html: string;
  onClippedChange: (clipped: boolean) => void;
}) {
  const srcDoc = `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob:; style-src 'unsafe-inline';"><style>html,body{margin:0;min-height:100%;overflow:hidden;background:white;color:#171717;font:14px/1.4 system-ui,sans-serif}body{padding:12px;box-sizing:border-box}*{box-sizing:border-box;max-width:100%}</style></head><body>${html}</body></html>`;

  return (
    <iframe
      data-testid="run-table-html-preview"
      title={t('fileRenderer.html.iframeTitle', { ns: 'file-renderer' })}
      srcDoc={srcDoc}
      sandbox=""
      referrerPolicy="no-referrer"
      loading="lazy"
      className="pointer-events-none -m-3 block h-[84px] w-[calc(100%+1.5rem)] border-0 bg-white"
      tabIndex={-1}
      onLoad={event => {
        const documentElement =
          event.currentTarget.contentDocument?.documentElement;
        onClippedChange(
          Boolean(
            documentElement &&
              documentElement.scrollHeight >
                event.currentTarget.clientHeight + 1,
          ),
        );
      }}
    />
  );
}

function PreviewContent({
  value,
  presentation,
  onNestedClippedChange,
  t,
  variant,
}: {
  value: TResolvedValue;
  presentation: IOutputPresentation;
  onNestedClippedChange: (clipped: boolean) => void;
  t: TFunction;
  variant: 'table' | 'bare';
}) {
  if (isRemoteValue(value)) {
    return (
      <Text level="xSmall" color="muted">
        {presentation.summary}
      </Text>
    );
  }

  switch (presentation.variant) {
    case EOutputCardVariant.NUMBER:
      return (
        <div className="text-left text-sm leading-5 font-medium tabular-nums">
          {presentation.summary}
        </div>
      );
    case EOutputCardVariant.EMPTY:
      return (
        <Flex align="center" justify="center" gap={2} className="h-full">
          <Text level="xSmall" color="muted">
            {t('outputs.card.noValue')}
          </Text>
        </Flex>
      );
    case EOutputCardVariant.TEXT:
      return (
        <div className="whitespace-pre-wrap break-words text-xs leading-4">
          {String(value)}
        </div>
      );
    case EOutputCardVariant.TABLE:
      return <TableCellPreview value={value} presentation={presentation} />;
    case EOutputCardVariant.IDP:
      return <IDPValueThumbnail value={value} className={PREVIEW_HEIGHT} />;
    case EOutputCardVariant.LIST: {
      const names = getOutputFileNames(value);
      const lines = names.slice(0, 2);
      if (names.length > 2) {
        lines.push(t('outputs.actions.showNMore', { count: names.length - 2 }));
      }
      return <PreviewLines lines={lines} />;
    }
    case EOutputCardVariant.FILE: {
      const isHtmlFile =
        detectFileType(getResolvedFileDisplayName(value) ?? '') ===
        EFileType.HTML;
      return (
        <div data-testid="run-table-document-preview">
          {variant === 'table' && isHtmlFile && isFile(value) ? (
            <HtmlFileCellPreview value={value} />
          ) : (
            <OutputMediaPreview
              value={value}
              className={PREVIEW_HEIGHT}
              // Other documents retain lightweight table placeholders.
              silhouetteOnly={variant === 'table'}
            />
          )}
          <span className="sr-only">
            {getReadableFileName(presentation.summary)}
          </span>
        </div>
      );
    }
    case EOutputCardVariant.CODE:
      if (typeof value === 'string' && isHtmlString(value)) {
        return (
          <HtmlCellPreview
            html={value}
            onClippedChange={onNestedClippedChange}
            t={t}
          />
        );
      }
      if (isMarkdownString(value)) {
        return (
          <div
            className={cn('pointer-events-none', MARKDOWN_PREVIEW_CLASSNAME)}
            data-testid="markdown-preview"
          >
            <Markdown size="sm" textProps={{ level: 'xSmall' }}>
              {showAutolinksLiterally(
                value
                  .split('\n')
                  .slice(0, MARKDOWN_PREVIEW_MAX_LINES)
                  .join('\n'),
                findAutolinks(value),
              )}
            </Markdown>
          </div>
        );
      }
      {
        const lines = getOutputSourceText(value).split('\n');
        return <SyntaxPreviewLines lines={lines.slice(0, 4)} />;
      }
    case EOutputCardVariant.DICT: {
      const lines = stringify(value, OUTPUT_YAML_OPTIONS).trimEnd().split('\n');
      return <SyntaxPreviewLines lines={lines.slice(0, 4)} />;
    }
    case EOutputCardVariant.BROWSER:
      return (
        <PreviewLines
          lines={[presentation.summary, t('outputs.card.browserReplay')]}
        />
      );
  }
}

export function RunTableOutputPreview({
  name,
  value,
  onOpen,
  variant = 'table',
  renderInline = false,
  className,
}: IRunTableOutputPreviewProps) {
  // Subscribing through the hook is the point, not the lookup: a bare
  // `i18n.t()` runs once and keeps whatever it got, so a cell rendered before
  // i18next finished initialising kept the raw key forever.
  const { t } = useTranslation('outputs');
  const presentation = getOutputPresentation(value);
  const isInline = renderInline && shouldRenderInline(value);
  // An empty cell has nothing to open, so it is not offered as clickable.
  const openHandler =
    presentation.variant === EOutputCardVariant.EMPTY ? undefined : onOpen;
  const [previewElement, setPreviewElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [nestedClipped, setNestedClipped] = useState(false);
  const isHtml = typeof value === 'string' && isHtmlString(value);
  const rendererClipped = (() => {
    if (isRemoteValue(value)) return false;
    switch (presentation.variant) {
      case EOutputCardVariant.TABLE:
        return getOutputTablePreview(value).rows.length > 2;
      case EOutputCardVariant.CODE:
        // Markdown renders far more than 4 lines, so only the measured overflow applies.
        if (isHtml || isMarkdownString(value)) return false;
        return getOutputSourceText(value).split('\n').length > 4;
      case EOutputCardVariant.DICT:
        return (
          stringify(value, OUTPUT_YAML_OPTIONS).trimEnd().split('\n').length > 4
        );
      default:
        return false;
    }
  })();
  const isOverflowing =
    useIsOverflowing(previewElement) ||
    rendererClipped ||
    (isHtml && nestedClipped);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // An inline renderer's own controls handle their keys; don't also open.
    if (event.target !== event.currentTarget) return;
    if (!openHandler || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    openHandler();
  };

  return (
    <div
      className={cn(
        'w-full min-w-0',
        variant === 'table' && 'pt-3 pr-4 pb-4 pl-3',
        className,
      )}
    >
      <div className="relative rounded-lg">
        <div
          ref={setPreviewElement}
          className={cn(
            'rounded-lg bg-muted/50 text-left outline-none',
            // `bare` scrolls taller instead of clipping to the layover; no
            // bar, which would read as a second control inside one big
            // button. overflow-x stays hidden either way — `overflow-y-auto`
            // alone computes it to auto, and these previews bleed past the
            // padding by design. `no-scrollbars-within`, not `[scrollbar-width:none]`:
            // the global `*` scrollbar rule is unlayered and beats utilities.
            isInline
              ? 'overflow-hidden'
              : variant === 'bare'
                ? 'no-scrollbars-within max-h-[150px] overflow-x-hidden overflow-y-auto'
                : cn(PREVIEW_MAX_HEIGHT, 'overflow-hidden'),
            isInline || presentation.variant === EOutputCardVariant.FILE
              ? 'p-0'
              : 'p-3',
            openHandler &&
              'cursor-pointer focus-visible:ring-2 focus-visible:ring-ring',
          )}
          role={openHandler ? 'button' : undefined}
          tabIndex={openHandler ? 0 : undefined}
          aria-label={
            openHandler ? t('outputs.card.openPreview', { name }) : undefined
          }
          onClick={openHandler}
          onKeyDown={handleKeyDown}
          data-output-variant={presentation.variant}
          data-testid="run-table-preview-box"
        >
          {isInline ? (
            <Suspense
              fallback={
                <Flex align="center" justify="center" className="py-4">
                  <Spinner size="sm" />
                </Flex>
              }
            >
              <OutputRenderer
                value={value}
                mode={ERenderMode.PREVIEW}
                outputName={name}
                minimal
              />
            </Suspense>
          ) : (
            <PreviewContent
              value={value}
              presentation={presentation}
              onNestedClippedChange={setNestedClipped}
              t={t}
              variant={variant === 'compact' ? 'bare' : variant}
            />
          )}
        </div>
        {/* Outside the scroller, so the fade stays on the bottom edge instead
            of riding up with the content. */}
        {isOverflowing && <div className="output-preview__overflow-shadow" />}
      </div>
    </div>
  );
}
